import { graphql, useStaticQuery } from 'gatsby';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useScreenSize } from '../../../hooks/useScreenSize';
import { ParentPage, SanitySlug } from '../../../types/common';
import { ArrowDownIcon, DownIcon, FilterIcon, GlassIcon, SwapIcon } from '../../Icons';
import InternalLink from '../../InternalLink';
import './styles.scss';

type ProductionResearchDatabaseProps = {
  data: {
    title?: string | null;
  };
}

type FilterFields = {
  region: string;
  year: string;
  category: string;
  investigator: string;
}

// type Research = {
//   _id: string;
//   parentPage: ParentPage,
//   budgetId: string;
//   description: string;
//   region?: string;
//   year?: string;
//   category?: string;
//   investigator?: string;
//   slug: SanitySlug;
// }

type Columns = {
  budgetId: string;
  description: string;
  region?: string;
  year?: string;
  category?: string;
  investigator?: string;
}

type MappedResearch<T> = {
  [P in keyof T]: T[P];
}

type Research = MappedResearch<Columns> & {
  _id: string;
  parentPage: ParentPage,
  slug: SanitySlug;
};

type Ordenation = {
  column: keyof Columns;
  order: 'ASC' | 'DESC';
}

type QueryData = {
  allSanityProductionResearch: {
    nodes: Research[]
  };

}

const ProductionResearchDatabase: FunctionComponent<ProductionResearchDatabaseProps> = ({ data }) => {
  const { isMobile } = useScreenSize();

  const query = (useStaticQuery<QueryData>(graphql`
    {
      allSanityProductionResearch {
        nodes {
          _id
          parentPage {
            slug {
              current
            }
          }
          budgetId
          description
          region
          year
          category
          investigator
          slug {
            current
          }
        }
      }
    }
  `));
  const fullList = query.allSanityProductionResearch.nodes;

  const [searchTerm, setSearchTerm] = useState('');
  const [linesQuantity, setLinesQuantity] = useState(10);
  const [budgetSearch, setBudgetSearch] = useState('');
  const [ordenation, setOrdernation] = useState<Ordenation>({
    column: 'budgetId',
    order: 'ASC',
  });
  const [pagination, setPagination] = useState<number>(1);
  const [paginationInput, setPaginationInput] = useState<string>(pagination.toString());
  const [filteredList, setFilteredList] = useState(sortList(fullList, ordenation));
  const [renderedList, setRenderedList] = useState(filteredList.slice(0, linesQuantity));
  const [filters, setFilters] = useState<FilterFields>({
    region: '',
    year: '',
    category: '',
    investigator: '',
  })

  const maxPagination = Math.ceil(filteredList.length / linesQuantity);

  useEffect(() => {
    const list = sortList(filteredList, ordenation);
    setFilteredList(list);
    setRenderedList(list.slice(0, linesQuantity))
    setPagination(1);
  }, [ordenation]);

  useEffect(() => {
    setRenderedList(filteredList.slice((pagination - 1) * linesQuantity, pagination * linesQuantity));
  }, [pagination, linesQuantity]);

  useEffect(() => {
    setPaginationInput(pagination.toString());
  }, [pagination]);

  useEffect(() => {
    if (!paginationInput) return;
    let page = parseInt(paginationInput);

    if (page > maxPagination) {
      page = maxPagination;
      setPaginationInput(page.toString());
    }

    if (page < 1) {
      page = 1;
      setPaginationInput(page.toString());
    }

    setPagination(page);
  }, [paginationInput]);


  useEffect(() => {
    if (!budgetSearch && !searchTerm && !filters.region && !filters.year && !filters.category && !filters.investigator) {
      return setFilteredList(fullList);
    }

    let list = fullList.filter(item => item.budgetId.includes(budgetSearch));

    if (filters.region) {
      list = list.filter(item => item.region === filters.region);
    }

    if (filters.year) {
      list = list.filter(item => item.year === filters.year);
    }

    if (filters.category) {
      list = list.filter(item => item.category === filters.category);
    }

    if (filters.investigator) {
      list = list.filter(item => item.investigator === filters.investigator);
    }

    list = list.filter(item => {
      if (item.budgetId.includes(searchTerm)) return true;
      if (item.description.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())) return true;
      if (item.region?.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())) return true;
      if (item.year?.includes(searchTerm)) return true;
      if (item.category?.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())) return true;
      if (item.investigator?.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())) return true;
      return false;
    });

    setFilteredList(list);
    setRenderedList(list.slice(0, linesQuantity));
    setPagination(1);
  }, [budgetSearch, searchTerm, filters]);

  function sortList(list: Research[], order: Ordenation): Research[] {
    let orderAux = [1, -1];
    if (order.order === 'DESC') {
      orderAux = [-1, 1];
    }

    const dataIsNumber = order.column === 'budgetId' || order.column === 'year';

    return list.sort((a, b) => {
      const auxA = a[order.column];
      const auxB = b[order.column];

      if (!auxA) return orderAux[1];
      if (!auxB) return orderAux[0];

      if (dataIsNumber) {
        const numberA = parseInt(auxA);
        const numberB = parseInt(auxB);

        if (numberA > numberB) return orderAux[0];
        if (numberA < numberB) return orderAux[1];
        return 0;
      }

      if (auxA.toLocaleLowerCase() > auxB.toLocaleLowerCase()) return orderAux[0];
      if (auxA.toLocaleLowerCase() < auxB.toLocaleLowerCase()) return orderAux[1];
      return 0;
    })
  }

  function handleOrdenation(column: keyof Columns) {
    setOrdernation(oldState => ({
      column,
      order: column === oldState.column ? (
        oldState.order === 'ASC' ? 'DESC' : 'ASC'
      ) : 'ASC',
    }))
  }

  function handlePagination() {
    if (!paginationInput) {
      setPaginationInput(pagination.toString());
    }
  }

  function sortItems(items: (string | undefined)[]): string[] {
    return items.map(item => !item ? '' : item).sort((a, b) => {
      return a.toLowerCase() > b.toLowerCase() ? 1 : -1;
    });
  }

  const regionList = sortItems([...new Set(fullList.map(item => item.region))]);
  const yearList = sortItems([...new Set(fullList.map(item => item.year))]);
  const categoryList = sortItems([...new Set(fullList.map(item => item.category))]);
  const investigatorList = sortItems([...new Set(fullList.map(item => item.investigator))]);

  return (
    <section className='wrapper production-research-db'>
      <h2>{data.title ? data.title : 'Production Research Database'}</h2>

      <div className='database-header'>
        <div className='search-bar'>
          <GlassIcon size={20} />
          <input
            name='search'
            placeholder={isMobile ? 'Search' : 'Search on the database'}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        <div className='show-entries'>
          <span>Show</span>
          <label>
            <select
              value={linesQuantity.toString()}
              onChange={e => setLinesQuantity(parseInt(e.target.value))}
            >
              <option>10</option>
              <option>25</option>
              <option>50</option>
              <option>100</option>
            </select>
            <DownIcon />
          </label>
          <span>Entries</span>
        </div>
      </div>

      <div className='database'>
        <table>
          <thead>
            <tr>
              <th className='budget'>
                <div>
                  <input
                    className='budgetInput'
                    placeholder='Budget ID'
                    value={budgetSearch}
                    onChange={e => setBudgetSearch(e.target.value)}
                  />
                  <button
                    onClick={() => handleOrdenation('budgetId')}
                    className={`ordenation ${ordenation.column === 'budgetId' ? ordenation.order : ''}`}
                  >
                    {ordenation.column === 'budgetId' ? <ArrowDownIcon /> : <SwapIcon />}
                  </button>
                </div>
              </th>
              <th>
                <div>
                  <span>Project description</span>
                  <button
                    onClick={() => handleOrdenation('description')}
                    className={`ordenation ${ordenation.column === 'description' ? ordenation.order : ''}`}
                  >
                    {ordenation.column === 'description' ? <ArrowDownIcon /> : <SwapIcon />}
                  </button>
                </div>
              </th>
              <th>
                <div>
                  <label>
                    <span><FilterIcon /></span>
                    <select
                      value={filters.region}
                      onChange={e => setFilters(oldFilters => ({ ...oldFilters, region: e.target.value }))}
                    >
                      <option value=''>State</option>
                      {regionList.map(item => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </label>
                  <button
                    onClick={() => handleOrdenation('region')}
                    className={`ordenation ${ordenation.column === 'region' ? ordenation.order : ''}`}
                  >
                    {ordenation.column === 'region' ? <ArrowDownIcon /> : <SwapIcon />}
                  </button>
                </div>
              </th>
              <th>
                <div>
                  <label>
                    <span><FilterIcon /></span>
                    <select
                      value={filters.year}
                      onChange={e => setFilters(oldFilters => ({ ...oldFilters, year: e.target.value }))}
                    >
                      <option value=''>Year</option>
                      {yearList.map(item => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </label>
                  <button
                    onClick={() => handleOrdenation('year')}
                    className={`ordenation ${ordenation.column === 'year' ? ordenation.order : ''}`}
                  >
                    {ordenation.column === 'year' ? <ArrowDownIcon /> : <SwapIcon />}
                  </button>
                </div>
              </th>
              <th>
                <div>
                  <label>
                    <span><FilterIcon /></span>
                    <select
                      value={filters.category}
                      onChange={e => setFilters(oldFilters => ({ ...oldFilters, category: e.target.value }))}
                    >
                      <option value=''>Category</option>
                      {categoryList.map(item => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </label>
                  <button
                    onClick={() => handleOrdenation('category')}
                    className={`ordenation ${ordenation.column === 'category' ? ordenation.order : ''}`}
                  >
                    {ordenation.column === 'category' ? <ArrowDownIcon /> : <SwapIcon />}
                  </button>
                </div>
              </th>
              <th>
                <div>
                  <label>
                    <span><FilterIcon /></span>
                    <select
                      value={filters.investigator}
                      onChange={e => setFilters(oldFilters => ({ ...oldFilters, investigator: e.target.value }))}
                    >
                      <option value=''>Investigator</option>
                      {investigatorList.map(item => <option key={item} value={item}>{item}</option>)}
                    </select>
                  </label>
                  <button
                    onClick={() => handleOrdenation('investigator')}
                    className={`ordenation ${ordenation.column === 'investigator' ? ordenation.order : ''}`}
                  >
                    {ordenation.column === 'investigator' ? <ArrowDownIcon /> : <SwapIcon />}
                  </button>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {renderedList.map(item => (
              <tr key={item._id}>
                <td className='budget'>
                  <InternalLink reference={item}>
                    {item.budgetId}
                  </InternalLink>
                </td>
                <td>{item.description}</td>
                <td>{item.region}</td>
                <td>{item.year}</td>
                <td>{item.category}</td>
                <td>{item.investigator}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className='pagination'>
        <button
          disabled={pagination <= 1}
          onClick={() => setPagination(prev => prev - 1)}
          className='prev'
        >
          <DownIcon />
        </button>
        <input
          value={paginationInput}
          onBlur={handlePagination}
          onChange={e => setPaginationInput(e.target.value.replace(/\D/g, ''))}
        />
        <button
          disabled={pagination >= maxPagination}
          onClick={() => setPagination(prev => prev + 1)}
          className='next'
        >
          <DownIcon />
        </button>
      </div>

    </section>
  );
}

export default ProductionResearchDatabase;
